import type { ClimateState, Environment, PlaceSst, RegionState, Sanctuary, World } from '../types';
import { REGIONS, clockLatitudeDeg, region } from '../data/oceans';
import { REGION_WATER } from '../data/water/woa23';
import { REGION_LIGHT } from '../data/light/light';
import { lightOf, waterOf } from '../data/sites';
import { dayLengthHours, noonElevationDeg, noonUtc, solarDeclinationDeg } from '../astronomy';
import { epochDayOf } from '../calendar';
import { held } from '../held';
import { mixedLayerOn, monthBlend, surfaceOn, type MonthBlend, type WaterClimatology } from '../waterColumn';
import { kdParOn, surfaceParOn, type LightClimatology } from '../light';
import { departureC, openingSst, regionRecord, sanctuaryRecord } from './climate';
import type { SstRecord } from '../sstRecord';

/**
 * Step 1 of the tick, the environment, for every region and every sanctuary,
 * whatever the player is looking at (`GDD.md` principle 7, persistent global
 * simulation).
 *
 * The sun, read at noon UTC (`astronomy.ts`), each region's at the middle
 * latitude of its band (`clockLatitudeDeg`), each sanctuary's at its own site;
 * and the water (M19), each region's mean water column and each aquarium's
 * own (`waterColumn.ts`), the mean year for the day, with the surface moved by
 * the year's departure from it (M24, `climate.ts`, which runs first and does
 * the drawing); and the light (M33), each place's sunlight at the surface and
 * clarity, the mean year for the day (`light.ts`). Phase 1 adds ice. Nothing
 * here draws.
 */
export function stepEnvironment(world: World): void {
  const today = epochDayOf(world);
  // One sun for the day, read at every latitude, and one blend of two months, read in every water column.
  const declination = solarDeclinationDeg(noonUtc(today));
  const blend = monthBlend(today);
  for (const state of world.regions) {
    state.env = environment(clockLatitudeDeg(region(state.id)), REGION_WATER[state.id], REGION_LIGHT[state.id], declination, blend, departureC(regionRecord(state.id), state.sst, today));
  }
  for (const s of world.sanctuaries) {
    s.env = environment(s.site.latDeg, waterOf(s), lightOf(s), declination, blend, departureC(sanctuaryRecord(s), s.sst, today));
  }
}

/** A region's environment on a date (an epoch day), with its departure read as its `sst` says. */
export function regionEnvironment(state: Pick<RegionState, 'id' | 'sst'>, epochDay: number): Environment {
  return placeEnvironment(clockLatitudeDeg(region(state.id)), REGION_WATER[state.id], REGION_LIGHT[state.id], regionRecord(state.id), state.sst, epochDay);
}

/** A sanctuary's environment on a date, at its site, over its water. */
export function sanctuaryEnvironment(s: Pick<Sanctuary, 'site' | 'siteId' | 'regionId' | 'sst'>, epochDay: number): Environment {
  return placeEnvironment(s.site.latDeg, waterOf(s), lightOf(s), sanctuaryRecord(s), s.sst, epochDay);
}

/**
 * Any place's environment on a date: its latitude's sun, its water's mean
 * year, and its record's departure read as `sst` says. A new world and a
 * loaded save read their regions and sanctuaries with it, and so does the
 * hindcast at Station ALOHA, a place the game does not have (M28,
 * `scripts/hindcast.ts`); the tick steps the same arithmetic (`environment`)
 * with one sun and one blend for the day (review M30: this said the tick read
 * through it too).
 */
export function placeEnvironment(latDeg: number, water: WaterClimatology, light: LightClimatology, record: SstRecord, sst: PlaceSst, epochDay: number): Environment {
  return environment(latDeg, water, light, solarDeclinationDeg(noonUtc(epochDay)), monthBlend(epochDay), departureC(record, sst, epochDay));
}

/** A place's environment on a date in the mean year: no departure from its normal (the plankton's opening, M52). */
export function meanYearEnvironment(latDeg: number, water: WaterClimatology, light: LightClimatology, epochDay: number): Environment {
  return environment(latDeg, water, light, solarDeclinationDeg(noonUtc(epochDay)), monthBlend(epochDay), 0);
}

function environment(latDeg: number, water: WaterClimatology, light: LightClimatology, declination: number, blend: MonthBlend, departure: number): Environment {
  return {
    dayLengthHours: held(dayLengthHours(latDeg, declination)),
    noonSunDeg: held(noonElevationDeg(latDeg, declination)),
    seaSurfaceC: held(surfaceOn(water, 'temperature', blend) + departure),
    seaSurfaceAnomalyC: departure,
    mixedLayerM: held(mixedLayerOn(water, blend)),
    surfaceParMol: held(surfaceParOn(light, blend)),
    kdPar: held(kdParOn(light, blend)),
  };
}

/** The regions in their fixed order, each with the date's environment, for a new world (its plankton, `createWorld`'s). */
export function openingRegions(epochDay: number, climate: ClimateState): Array<Omit<RegionState, 'plankton'>> {
  return REGIONS.map((r) => {
    const sst = openingSst(regionRecord(r.id), climate, epochDay);
    return { id: r.id, env: regionEnvironment({ id: r.id, sst }, epochDay), sst };
  });
}
